import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const familyTherapyFieldPack: FieldPack = {
  id: 'genogram-family-therapy',
  name: 'Family Therapy',
  description: 'Fields for family systems therapy and relationship assessment',
  version: '1.0.0',
  dependencies: ['genogram-base'],
  fields: [
    {
      id: 'emotionalBonds',
      label: 'fields.therapy.emotionalBonds.label',
      description: 'fields.therapy.emotionalBonds.description',
      type: FieldType.SELECT,
      tags: ['relationships', 'therapy', 'family-dynamics'],
      priority: 95,
      options: [
        { value: 'very_close', label: 'fields.therapy.emotionalBonds.options.very_close' },
        { value: 'close', label: 'fields.therapy.emotionalBonds.options.close' },
        { value: 'distant', label: 'fields.therapy.emotionalBonds.options.distant' },
        { value: 'conflictual', label: 'fields.therapy.emotionalBonds.options.conflictual' },
        { value: 'cutoff', label: 'fields.therapy.emotionalBonds.options.cutoff' },
        { value: 'enmeshed', label: 'fields.therapy.emotionalBonds.options.enmeshed' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'attachmentStyle',
      label: 'fields.therapy.attachmentStyle.label',
      description: 'fields.therapy.attachmentStyle.description',
      type: FieldType.SELECT,
      tags: ['mental-health', 'therapy', 'behavioral'],
      priority: 90,
      options: [
        { value: 'secure', label: 'fields.therapy.attachmentStyle.options.secure' },
        { value: 'anxious', label: 'fields.therapy.attachmentStyle.options.anxious' },
        { value: 'avoidant', label: 'fields.therapy.attachmentStyle.options.avoidant' },
        { value: 'disorganized', label: 'fields.therapy.attachmentStyle.options.disorganized' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'communicationStyle',
      label: 'fields.therapy.communicationStyle.label',
      description: 'fields.therapy.communicationStyle.description',
      type: FieldType.SELECT,
      tags: ['relationships', 'therapy', 'family-dynamics'],
      priority: 85,
      options: [
        { value: 'assertive', label: 'fields.therapy.communicationStyle.options.assertive' },
        { value: 'passive', label: 'fields.therapy.communicationStyle.options.passive' },
        { value: 'aggressive', label: 'fields.therapy.communicationStyle.options.aggressive' },
        { value: 'passive_aggressive', label: 'fields.therapy.communicationStyle.options.passive_aggressive' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'copingStrategies',
      label: 'fields.therapy.copingStrategies.label',
      description: 'fields.therapy.copingStrategies.description',
      type: FieldType.MULTI_SELECT,
      tags: ['mental-health', 'therapy', 'behavioral'],
      priority: 80,
      options: [
        { value: 'healthy_adaptive', label: 'fields.therapy.copingStrategies.options.healthy_adaptive' },
        { value: 'avoidance', label: 'fields.therapy.copingStrategies.options.avoidance' },
        { value: 'substance_use', label: 'fields.therapy.copingStrategies.options.substance_use' },
        { value: 'social_support', label: 'fields.therapy.copingStrategies.options.social_support' },
        { value: 'problem_solving', label: 'fields.therapy.copingStrategies.options.problem_solving' },
        { value: 'denial', label: 'fields.therapy.copingStrategies.options.denial' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'familyRole',
      label: 'fields.therapy.familyRole.label',
      description: 'fields.therapy.familyRole.description',
      type: FieldType.SELECT,
      tags: ['relationships', 'therapy', 'family-dynamics'],
      priority: 75,
      options: [
        { value: 'hero', label: 'fields.therapy.familyRole.options.hero' },
        { value: 'scapegoat', label: 'fields.therapy.familyRole.options.scapegoat' },
        { value: 'lost_child', label: 'fields.therapy.familyRole.options.lost_child' },
        { value: 'mascot', label: 'fields.therapy.familyRole.options.mascot' },
        { value: 'caretaker', label: 'fields.therapy.familyRole.options.caretaker' },
        { value: 'peacemaker', label: 'fields.therapy.familyRole.options.peacemaker' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'traumaHistory',
      label: 'fields.therapy.traumaHistory.label',
      description: 'fields.therapy.traumaHistory.description',
      type: FieldType.MULTI_SELECT,
      tags: ['mental-health', 'therapy', 'behavioral'],
      priority: 70,
      options: [
        { value: 'none_reported', label: 'fields.therapy.traumaHistory.options.none_reported' },
        { value: 'childhood_abuse', label: 'fields.therapy.traumaHistory.options.childhood_abuse' },
        { value: 'domestic_violence', label: 'fields.therapy.traumaHistory.options.domestic_violence' },
        { value: 'loss_grief', label: 'fields.therapy.traumaHistory.options.loss_grief' },
        { value: 'combat', label: 'fields.therapy.traumaHistory.options.combat' },
        { value: 'accident', label: 'fields.therapy.traumaHistory.options.accident' },
        { value: 'neglect', label: 'fields.therapy.traumaHistory.options.neglect' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'mentalHealthTreatment',
      label: 'fields.therapy.mentalHealthTreatment.label',
      description: 'fields.therapy.mentalHealthTreatment.description',
      type: FieldType.MULTI_SELECT,
      tags: ['mental-health', 'therapy'],
      priority: 65,
      options: [
        { value: 'individual_therapy', label: 'fields.therapy.mentalHealthTreatment.options.individual_therapy' },
        { value: 'family_therapy', label: 'fields.therapy.mentalHealthTreatment.options.family_therapy' },
        { value: 'couples_therapy', label: 'fields.therapy.mentalHealthTreatment.options.couples_therapy' },
        { value: 'group_therapy', label: 'fields.therapy.mentalHealthTreatment.options.group_therapy' },
        { value: 'medication', label: 'fields.therapy.mentalHealthTreatment.options.medication' },
        { value: 'hospitalization', label: 'fields.therapy.mentalHealthTreatment.options.hospitalization' },
        { value: 'none', label: 'fields.therapy.mentalHealthTreatment.options.none' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'treatmentGoals',
      label: 'fields.therapy.treatmentGoals.label',
      description: 'fields.therapy.treatmentGoals.description',
      type: FieldType.MULTI_SELECT,
      tags: ['therapy', 'family-dynamics'],
      priority: 60,
      allowCustomOptions: true,
      options: [
        { value: 'improve_communication', label: 'fields.therapy.treatmentGoals.options.improve_communication' },
        { value: 'conflict_resolution', label: 'fields.therapy.treatmentGoals.options.conflict_resolution' },
        { value: 'boundary_setting', label: 'fields.therapy.treatmentGoals.options.boundary_setting' },
        { value: 'trauma_processing', label: 'fields.therapy.treatmentGoals.options.trauma_processing' },
        { value: 'grief_support', label: 'fields.therapy.treatmentGoals.options.grief_support' },
        { value: 'relationship_repair', label: 'fields.therapy.treatmentGoals.options.relationship_repair' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'intergenerationalPatterns',
      label: 'fields.therapy.intergenerationalPatterns.label',
      description: 'fields.therapy.intergenerationalPatterns.description',
      type: FieldType.MULTI_SELECT,
      tags: ['genetic', 'hereditary', 'therapy', 'family-dynamics'],
      priority: 68,
      allowCustomOptions: true,
      options: [
        { value: 'substance_abuse', label: 'fields.therapy.intergenerationalPatterns.options.substance_abuse' },
        { value: 'divorce', label: 'fields.therapy.intergenerationalPatterns.options.divorce' },
        { value: 'mental_illness', label: 'fields.therapy.intergenerationalPatterns.options.mental_illness' },
        { value: 'abuse', label: 'fields.therapy.intergenerationalPatterns.options.abuse' },
        { value: 'abandonment', label: 'fields.therapy.intergenerationalPatterns.options.abandonment' },
        { value: 'achievement', label: 'fields.therapy.intergenerationalPatterns.options.achievement' },
        { value: 'caregiving', label: 'fields.therapy.intergenerationalPatterns.options.caregiving' },
        { value: 'affected', label: 'fields.therapy.intergenerationalPatterns.options.affected' },
        { value: 'possibly_affected', label: 'fields.therapy.intergenerationalPatterns.options.possibly_affected' },
        { value: 'suspected_affected', label: 'fields.therapy.intergenerationalPatterns.options.suspected_affected' },
        { value: 'suspected_illness_pattern', label: 'fields.therapy.intergenerationalPatterns.options.suspected_illness_pattern' },
        { value: 'serious_illness_pattern', label: 'fields.therapy.intergenerationalPatterns.options.serious_illness_pattern' },
        { value: 'recovering_illness_pattern', label: 'fields.therapy.intergenerationalPatterns.options.recovering_illness_pattern' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'strengths',
      label: 'fields.therapy.strengths.label',
      description: 'fields.therapy.strengths.description',
      type: FieldType.MULTI_SELECT,
      tags: ['therapy', 'behavioral'],
      priority: 50,
      allowCustomOptions: true,
      options: [
        { value: 'resilient', label: 'fields.therapy.strengths.options.resilient' },
        { value: 'empathetic', label: 'fields.therapy.strengths.options.empathetic' },
        { value: 'self_aware', label: 'fields.therapy.strengths.options.self_aware' },
        { value: 'motivated', label: 'fields.therapy.strengths.options.motivated' },
        { value: 'supportive', label: 'fields.therapy.strengths.options.supportive' },
        { value: 'creative', label: 'fields.therapy.strengths.options.creative' },
      ],
      namespace: 'therapy',
    },
    {
      id: 'therapyNotes',
      label: 'fields.therapy.therapyNotes.label',
      description: 'fields.therapy.therapyNotes.description',
      type: FieldType.RICH_TEXT,
      tags: ['notes', 'documentation'],
      priority: 45,
      multiline: true,
      placeholder: 'fields.therapy.therapyNotes.placeholder',
      namespace: 'therapy',
    },
  ],
};
